import { standingOf } from '@novachat/client';
import { CHANNEL_PERMISSION_GROUPS, ROLE_TEMPLATES, type Channel, type ChannelPermission, type ChannelRoles, type Me, type RoleInfo } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ChevronDown, Plus, Shield, UserMinus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { client } from '../lib/client';
import { closeOnBackdrop } from '../lib/dismiss';
import { errorMessage } from '../lib/errors';
import { useLive } from '../state/live';
import { Avatar } from './Avatar';

/**
 * "Üyeler ve roller" (docs/plans/2026-11-03-channel-roles-design.md): who is in the channel and
 * with which roles, and what each role may do. Everyone sees it; what they can change follows
 * their own roles and the rank rule, which the server enforces again.
 */
export function PeopleDialog({ channel, me, onClose }: { channel: Channel; me: Pick<Me, 'id'>; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [tab, setTab] = useState<'people' | 'roles'>('people');
  useEffect(() => dialog.current?.showModal(), []);
  const roles = useQuery({ queryKey: ['roles', channel.id], queryFn: () => client.api.roles(channel.id) });
  const tabClass = (active: boolean) =>
    `h-9 rounded-sm border-0 px-3 text-sm font-semibold ${active ? 'bg-surface-3 text-fg-1' : 'bg-transparent text-fg-2 hover:text-fg-1'}`;

  return (
    <dialog
      onClick={closeOnBackdrop}
      ref={dialog}
      onClose={onClose}
      aria-label={`${channel.name} · ${channel.permissions.includes('edit_roles') ? 'Üyeler ve roller' : 'Üyeler'}`}
      className="m-auto w-[min(560px,calc(100vw-24px))] rounded-xl border border-line-strong bg-surface-1 p-0 text-fg-1 backdrop:bg-black/60"
    >
      <div className="flex items-start justify-between gap-3 px-6 pt-5">
        <div className="min-w-0">
          <h2 className="m-0 font-heading text-xl font-bold">{channel.permissions.includes('edit_roles') ? 'Üyeler ve roller' : 'Üyeler'}</h2>
          <p className="m-0 truncate text-[13px] text-fg-3">{channel.name}</p>
        </div>
        <button type="button" aria-label="Kapat" onClick={() => dialog.current?.close()} className="grid size-8 flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2">
          <X size={18} />
        </button>
      </div>
      <div role="tablist" className="mx-6 mt-3 flex gap-1 border-b border-line pb-2">
        <button type="button" role="tab" aria-selected={tab === 'people'} onClick={() => setTab('people')} className={tabClass(tab === 'people')}>
          Üyeler
        </button>
        {channel.permissions.includes('edit_roles') && (
          <button type="button" role="tab" aria-selected={tab === 'roles'} onClick={() => setTab('roles')} className={tabClass(tab === 'roles')}>
            Roller
          </button>
        )}
      </div>
      <div className="max-h-[70dvh] overflow-y-auto px-6 pt-4 pb-6">
        {roles.isError ? (
          <p role="alert" className="m-0 text-sm text-[#EE9191]">{errorMessage(roles.error)}</p>
        ) : !roles.data ? (
          <p className="m-0 text-sm text-fg-3">Yükleniyor…</p>
        ) : tab === 'people' ? (
          <People channel={channel} me={me} data={roles.data} close={() => dialog.current?.close()} />
        ) : (
          <Roles channel={channel} me={me} data={roles.data} />
        )}
      </div>
    </dialog>
  );
}

/** Runs one change, shows its error, and refreshes what it may have changed. */
function useRun(channelId: string) {
  const queries = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function run(action: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
      await Promise.all([
        queries.invalidateQueries({ queryKey: ['roles', channelId] }),
        queries.invalidateQueries({ queryKey: ['members', channelId] }),
        queries.invalidateQueries({ queryKey: ['channels'] }),
      ]);
    }
  }
  return { run, error, busy };
}

const chip = 'rounded-xs bg-surface-3 px-2 py-0.5 text-xs font-semibold text-fg-2';
const ErrorText = ({ text }: { text: string | null }) => (text ? <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">{text}</p> : null);

function People({ channel, me, data, close }: { channel: Channel; me: Pick<Me, 'id'>; data: ChannelRoles; close: () => void }) {
  const standing = standingOf(channel, me, data);
  const { can, below, rolesOf, rankOf, fallback } = standing;
  const isPrivate = channel.visibility === 'private';
  // Private: its members. Public: everyone who can use NovaChat and has not left it.
  const members = useQuery({ queryKey: ['members', channel.id], queryFn: () => client.api.members(channel.id) });
  const users = useQuery({ queryKey: ['users'], queryFn: () => client.api.users(), enabled: isPrivate && can('add_members') });
  const { run, error, busy } = useRun(channel.id);
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState<Set<string>>(new Set());

  const people = (members.data ?? [])
    .map((m) => ({ userId: m.userId, name: m.name }))
    .sort((a, b) => rankOf(b.userId) - rankOf(a.userId) || a.name.localeCompare(b.name, 'tr'));
  const inList = new Set(people.map((p) => p.userId));
  const candidates = (users.data ?? []).filter((u) => !inList.has(u.id));
  const soleOwner = standing.isOwner(me.id) && standing.owners === 1;

  async function leave() {
    if (await run(() => client.api.removeMember(channel.id, me.id))) {
      useLive.getState().select(null);
      close();
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <ul aria-label="Üyeler" className="m-0 flex list-none flex-col gap-1 p-0">
        {people.map((person) => {
          const own = rolesOf(person.userId);
          const self = person.userId === me.id;
          // Others below you (owners: anyone); yourself only to give roles up.
          const editable = self ? own.length > 0 : can('assign_roles') && below(person.userId);
          const open = openId === person.userId;
          return (
            <li key={person.userId} className="rounded-md">
              <div className="flex items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm">
                <Avatar id={person.userId} name={person.name} size={28} />
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="min-w-0 truncate">
                    {person.name}
                    {person.userId === me.id && <span className="text-fg-3"> (sen)</span>}
                  </span>
                  <span className="flex flex-wrap gap-1">
                    {own.map((role) => (
                      <span key={role.id} className={chip}>
                        {role.name}
                      </span>
                    ))}
                    {own.length === 0 && <span className={`${chip} font-normal text-fg-3`}>{fallback ? `${fallback.name} (varsayılan)` : 'Rolü yok'}</span>}
                  </span>
                </div>
                {editable && (
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={self ? 'Rollerim' : `${person.name} rolleri`}
                    title={self ? 'Rollerini bırak' : 'Rolleri'}
                    onClick={() => setOpenId(open ? null : person.userId)}
                    className={`nc-press flex h-8 flex-none items-center gap-1 rounded-sm border-0 px-2 text-[13px] font-semibold ${open ? 'bg-surface-3 text-fg-1' : 'bg-transparent text-fg-2 hover:bg-surface-2'}`}
                  >
                    <Shield size={15} aria-hidden="true" />
                    <span className="max-sm:hidden">Roller</span>
                    <ChevronDown size={14} aria-hidden="true" className={open ? 'rotate-180' : ''} />
                  </button>
                )}
                {!self && can('remove_members') && below(person.userId) && (
                  <button
                    type="button"
                    aria-label={`${person.name} kişisini çıkar`}
                    title={isPrivate ? 'Kanaldan çıkar' : 'Kanaldan çıkar (kendisi geri katılabilir)'}
                    disabled={busy}
                    onClick={() => void run(() => client.api.removeMember(channel.id, person.userId))}
                    className="grid size-8 flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-3 hover:bg-surface-2 hover:text-[#EE9191]"
                  >
                    <UserMinus size={16} />
                  </button>
                )}
              </div>
              {open && (
                <fieldset className="m-0 mb-2 ml-[46px] flex flex-col gap-0.5 rounded-md border border-line p-1.5">
                  <legend className="sr-only">{person.name} rolleri</legend>
                  {self && <p className="m-0 px-2 pt-1 pb-1.5 text-xs text-fg-3">Kendine rol veremezsin, yalnızca bırakabilirsin.{fallback ? ` Hepsini bırakırsan ${fallback.name} rolünde kalırsın.` : ''}</p>}
                  {data.roles.map((role) => {
                    const has = own.some((r) => r.id === role.id);
                    const allowed = self ? has && standing.mayDrop(role) : standing.mayHandle(role);
                    return (
                      <label key={role.id} className={`flex items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm ${allowed ? 'cursor-pointer hover:bg-surface-2' : 'opacity-50'}`}>
                        <input
                          type="checkbox"
                          checked={has}
                          disabled={!allowed || busy}
                          onChange={() => {
                            const next = has ? own.filter((r) => r.id !== role.id).map((r) => r.id) : [...own.map((r) => r.id), role.id];
                            void run(() => client.api.setMemberRoles(channel.id, person.userId, next));
                          }}
                          className="size-4 accent-brand-500"
                        />
                        <span className="flex-1">{role.name}</span>
                        {role.template === 'owner' && <span className="text-xs text-fg-3">Kanalın tüm yetkileri</span>}
                      </label>
                    );
                  })}
                </fieldset>
              )}
            </li>
          );
        })}
      </ul>

      {isPrivate && can('add_members') && candidates.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-line pt-4">
          <span className="text-[13px] font-semibold text-fg-2">Kişi ekle</span>
          <ul className="m-0 flex max-h-40 list-none flex-col overflow-y-auto rounded-md border border-line p-1">
            {candidates.map((person) => (
              <li key={person.id}>
                <label className="flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm hover:bg-surface-2">
                  <input
                    type="checkbox"
                    checked={adding.has(person.id)}
                    onChange={() =>
                      setAdding((current) => {
                        const next = new Set(current);
                        if (next.has(person.id)) next.delete(person.id);
                        else next.add(person.id);
                        return next;
                      })
                    }
                    className="size-4 accent-brand-500"
                  />
                  <Avatar id={person.id} name={person.name} size={24} />
                  {person.name}
                </label>
              </li>
            ))}
          </ul>
          <button
            type="button"
            disabled={adding.size === 0 || busy}
            onClick={() => void run(() => client.api.addMembers(channel.id, { userIds: [...adding] })).then((ok) => ok && setAdding(new Set()))}
            className="nc-press nc-cta h-10 self-end rounded-md border-0 bg-brand-500 px-4 text-sm font-bold text-white disabled:opacity-50"
          >
            {adding.size > 0 ? `${adding.size} kişiyi ekle` : 'Ekle'}
          </button>
        </div>
      )}

      <ErrorText text={error} />
      {soleOwner ? (
        <p className="m-0 rounded-md border border-line bg-surface-2 px-3 py-2.5 text-[13px] leading-normal text-fg-2">
          Bu kanalın tek sahibisin, o yüzden ayrılamazsın. Önce birine <span className="font-semibold text-fg-1">Sahip</span> rolünü ver ya da kanalı arşivle.
        </p>
      ) : (
        <button type="button" disabled={busy} onClick={() => void leave()} className="nc-press h-10 self-start rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold text-[#EE9191]">
          Kanaldan ayrıl
        </button>
      )}
    </div>
  );
}

function Roles({ channel, me, data }: { channel: Channel; me: Pick<Me, 'id'>; data: ChannelRoles }) {
  const standing = standingOf(channel, me, data);
  const { can, myRank } = standing;
  const { run, error, busy } = useRun(channel.id);
  const [openId, setOpenId] = useState<string | null>(null);
  const editable = standing.mayEdit;

  // New roles sit between Moderatör and Üye, or just under the caller when they rank lower.
  const addRole = () =>
    run(async () => {
      // Üye's permissions (those the caller holds): a role given on top must not quietly take away writing.
      const permissions = ROLE_TEMPLATES.find((t) => t.key === 'member')!.permissions.filter((p) => channel.permissions.includes(p));
      const { id } = await client.api.createRole(channel.id, { name: 'Yeni rol', permissions, position: Math.min(50, myRank - 1) });
      setOpenId(id);
    });

  return (
    <div className="flex flex-col gap-4">
      {data.defaultRoleId && (
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-fg-2">{channel.visibility === 'public' ? 'Rolü olmayanlar' : 'Rolü olmayan üyeler'}</span>
          <select
            value={data.defaultRoleId ?? ''}
            disabled={!can('edit_roles') || busy}
            onChange={(e) => void run(() => client.api.setDefaultRole(channel.id, e.target.value))}
            className="h-[42px] rounded-md border border-line-strong bg-surface-2 px-3 text-base text-fg-1 focus:border-brand-400 focus:outline-none disabled:opacity-70 sm:text-[15px]"
          >
            {data.roles
              .filter((role) => role.template !== 'owner' && (role.id === data.defaultRoleId || role.position < myRank))
              .map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
          </select>
          <span className="text-xs text-fg-3">
            {channel.visibility === 'public'
              ? 'Kendisine rol verilmemiş herkes bu rolde olur. Örneğin duyuru kanalı için Okuyucu.'
              : 'Bütün rolleri alınan ya da tek rolü silinen üye bu rolde olur.'}
          </span>
        </label>
      )}

      {can('edit_roles') && <p className="m-0 text-xs leading-normal text-fg-3">Üstteki rol daha yetkilidir: kimse kendi rolünün üstündeki rolleri ve kişileri değiştiremez. Okla sırayı değiştirebilirsin.</p>}
      <ul aria-label="Roller" className="m-0 flex list-none flex-col gap-1.5 p-0">
        {standing.ordered.map((role) => {
          const open = openId === role.id;
          const count = data.members.filter((m) => m.roleIds.includes(role.id)).length;
          const step = 'nc-press grid size-8 flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-3 hover:text-fg-1 disabled:opacity-30 max-md:size-10';
          return (
            <li key={role.id} className="rounded-md border border-line">
              <div className="flex items-center pr-1.5">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => setOpenId(open ? null : role.id)}
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-md border-0 bg-transparent px-3 py-2.5 text-left text-sm text-fg-1 hover:bg-surface-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{role.name}</span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-xs text-fg-3">
                      {count} kişi
                      {role.id === data.defaultRoleId && <span className={chip}>Varsayılan</span>}
                    </span>
                  </span>
                  <ChevronDown size={15} aria-hidden="true" className={`flex-none text-fg-3 ${open ? 'rotate-180' : ''}`} />
                </button>
                {can('edit_roles') && (
                  <>
                    <button type="button" aria-label={`Yukarı taşı: ${role.name}`} disabled={busy || !standing.mayMove(role, 'up')} onClick={() => void run(() => client.api.moveRole(channel.id, role.id, 'up'))} className={step}>
                      <ArrowUp size={15} aria-hidden="true" />
                    </button>
                    <button type="button" aria-label={`Aşağı taşı: ${role.name}`} disabled={busy || !standing.mayMove(role, 'down')} onClick={() => void run(() => client.api.moveRole(channel.id, role.id, 'down'))} className={step}>
                      <ArrowDown size={15} aria-hidden="true" />
                    </button>
                  </>
                )}
              </div>
              {open && (
                <RoleEditor
                  key={`${role.id}:${role.name}:${role.permissions.join()}`}
                  role={role}
                  editable={editable(role)}
                  removable={editable(role) && role.id !== data.defaultRoleId}
                  mine={channel.permissions}
                  busy={busy}
                  onSave={(body) => void run(() => client.api.updateRole(channel.id, role.id, body))}
                  onRemove={() => void run(() => client.api.removeRole(channel.id, role.id)).then((ok) => ok && setOpenId(null))}
                />
              )}
            </li>
          );
        })}
      </ul>
      <ErrorText text={error} />
      {can('edit_roles') && myRank > 1 && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void addRole()}
          className="nc-press flex h-10 items-center gap-1.5 self-start rounded-md border border-line-strong bg-surface-2 px-3.5 text-sm font-semibold disabled:opacity-60"
        >
          <Plus size={16} aria-hidden="true" />
          Yeni rol
        </button>
      )}
    </div>
  );
}

function RoleEditor({
  role,
  editable,
  removable,
  mine,
  busy,
  onSave,
  onRemove,
}: {
  role: RoleInfo;
  editable: boolean;
  removable: boolean;
  /** The caller's own permissions: nobody adds to a role one they lack. */
  mine: ChannelPermission[];
  busy: boolean;
  onSave: (body: { name: string; permissions: ChannelPermission[] }) => void;
  onRemove: () => void;
}) {
  const [name, setName] = useState(role.name);
  const [chosen, setChosen] = useState(() => new Set(role.permissions));
  const changed = name.trim() !== role.name || chosen.size !== role.permissions.length || role.permissions.some((p) => !chosen.has(p));
  const owner = role.template === 'owner';

  return (
    <div className="flex flex-col gap-3 border-t border-line px-3 pt-3 pb-3">
      {owner && <p className="m-0 text-[13px] text-fg-3">Sahip rolü kanalın bütün yetkilerine sahiptir; değiştirilemez.</p>}
      {!owner && !editable && <p className="m-0 text-[13px] text-fg-3">Bu rolü değiştiremezsin: senin rolünle aynı ya da daha üst seviyede, veya rolleri düzenleme yetkin yok.</p>}
      {editable && (
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-fg-2">Rol adı</span>
          <input
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            className="h-10 rounded-md border border-line-strong bg-surface-2 px-3 text-base text-fg-1 focus:border-brand-400 focus:outline-none sm:text-[15px]"
          />
        </label>
      )}
      {CHANNEL_PERMISSION_GROUPS.map((group) => (
        <fieldset key={group.title} className="m-0 flex flex-col gap-0.5 border-0 p-0">
          <legend className="mb-1 p-0 text-xs font-semibold text-fg-3">{group.title}</legend>
          {group.permissions.map(({ key, label }) => {
            const on = owner || chosen.has(key);
            // Taking away is always fine; adding needs the permission yourself.
            const allowed = editable && (on || mine.includes(key));
            return (
              <label key={key} className={`flex items-center gap-2.5 rounded-sm px-1.5 py-1 text-sm ${allowed ? 'cursor-pointer hover:bg-surface-2' : ''} ${!allowed && !on ? 'opacity-50' : ''}`}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!allowed}
                  onChange={() =>
                    setChosen((current) => {
                      const next = new Set(current);
                      if (next.has(key)) next.delete(key);
                      else next.add(key);
                      return next;
                    })
                  }
                  className="size-4 accent-brand-500"
                />
                {label}
              </label>
            );
          })}
        </fieldset>
      ))}
      {editable && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {removable && (
            <button type="button" disabled={busy} onClick={onRemove} className="nc-press mr-auto h-9 rounded-md border border-line-strong bg-surface-2 px-3 text-sm font-semibold text-[#EE9191] disabled:opacity-60">
              Rolü sil
            </button>
          )}
          <button
            type="button"
            disabled={!changed || !name.trim() || busy}
            onClick={() => onSave({ name: name.trim(), permissions: [...chosen] })}
            className="nc-press nc-cta h-9 rounded-md border-0 bg-brand-500 px-4 text-sm font-bold text-white disabled:opacity-50"
          >
            Kaydet
          </button>
        </div>
      )}
    </div>
  );
}
